import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@/auth/schemas';
import { useForgotPassword } from '@/hooks/useAuthActions';
import { useNotify } from '@/components/common/useNotify';
import { storage } from '@/lib/storage';

/**
 * The Tripza reset-password card — the sign-in card's twin.
 *
 * Markup and class names are LoginCard's, deliberately: the two pages sit one click apart, and a
 * person who has just failed to sign in should not feel they have landed somewhere else. Only the
 * avatar, the words and the fields differ.
 *
 * The logic is unchanged from the page this replaced — same schema, same hook, same submit. This is
 * a change of clothes, not of behaviour.
 */
export default function ForgotPasswordCard() {
  const notify = useNotify();
  const forgot = useForgotPassword();

  const { control, handleSubmit, formState: { errors } } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      tenantCode: import.meta.env.VITE_DEFAULT_TENANT || storage.tenantCode || '',
      email: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await forgot.mutateAsync({
        email: values.email.trim().toLowerCase(),
        tenantCode: values.tenantCode?.trim() || storage.tenantCode || null,
      });
    } catch (err) {
      notify.error(err);
    }
  });

  const busy = forgot.isPending;

  return (
    <section className="panel">
      <div className="card">
        <div className="avatar">🔑</div>
        <h2>Reset your password</h2>
        <p className="sub">
          Enter your company code and email — we&apos;ll send you a secure link to set a new one.
        </p>

        {forgot.isSuccess ? (
          <>
            {/*
              Deliberately vague about whether the account exists. Saying "no such email" would let
              anybody use this box to find out who has an account here.
            */}
            <div className="formmsg show good" role="status">
              If an account exists for that email, a reset link is on its way. Check your inbox —
              including spam — and follow the link to set a new password.
            </div>

            <p className="backlink">
              <RouterLink to="/login">← Back to sign in</RouterLink>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <Controller
              name="tenantCode"
              control={control}
              render={({ field }) => (
                <div className="field">
                  <label htmlFor="tenantCode">Company code</label>
                  <div className="inp">
                    <span className="ic">🏢</span>
                    <input
                      {...field}
                      value={field.value ?? ''}
                      id="tenantCode"
                      type="text"
                      placeholder="e.g. acme-travel"
                      autoComplete="organization"
                    />
                  </div>
                  <div className="hint">
                    Provided by your company administrator. Leave blank if your company has its own
                    web address.
                  </div>
                </div>
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <div className={`field${errors.email ? ' invalid' : ''}`}>
                  <label htmlFor="email">Email</label>
                  <div className="inp">
                    <span className="ic">✉️</span>
                    <input
                      {...field}
                      id="email"
                      type="email"
                      placeholder="you@tripza.in"
                      autoComplete="username"
                    />
                  </div>
                  {/*
                    The message comes from the schema rather than being hard-coded like the login
                    card's, so a change to the rule cannot leave the words behind saying something
                    else.
                  */}
                  <div className="err">
                    {errors.email?.message ?? 'Enter a valid email address.'}
                  </div>
                </div>
              )}
            />

            <button type="submit" className={`submit${busy ? ' loading' : ''}`} disabled={busy}>
              <span className="spin" />
              <span className="lbl">{busy ? 'Sending reset link…' : 'Send reset link →'}</span>
            </button>

            <p className="backlink">
              <RouterLink to="/login">← Back to sign in</RouterLink>
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
